// ManageSelf.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { apiFetch, BASE_URL } from "../api_call";
import { IoIosInformationCircle } from "react-icons/io";
import "./Dashboard.css";

export default function ManageSelf() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [compliances, setCompliances] = useState([]);
  const [files, setFiles] = useState({});
  const [successMsg, setSuccessMsg] = useState("");
  const [successVisible, setSuccessVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // ---------------- Fetch Regulatory Compliances ----------------
  const fetchCompliances = async () => {
  try {
    setLoading(true);
    setErrorMsg("");

    const complianceId = localStorage.getItem("complianceId");
    if (!complianceId) {
      setCompliances([]);
      return;
    }

    const res = await apiFetch(
      `/compliance/fetch/regulatory/${complianceId}`,
      { method: "GET" }
    );

    const records = Array.isArray(res?.data) ? res.data : [];

    const formatted = records.map((row) => ({
      regcmp_id: row.regcmp_id,
      particular: row.regcmp_particular,
      act: row.regcmp_act,
      title: row.regcmp_title,
      long_description: row.regcmp_long_description,
      description: row.regcmp_description,
      start_date: row.regcmp_start_date,
      action_date: row.regcmp_action_date,
      end_date: row.regcmp_end_date,
      reminder: `${row.regcmp_reminder_days} Days`,
      status: row.regcmp_status,
      approver_email: row.regcmp_approvers_email || "",
    }));

    setCompliances(formatted);
  } catch (err) {
    console.error("Failed to fetch compliance data:", err);
    setErrorMsg("❌ Failed to fetch compliances");
    setCompliances([]);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchCompliances();
  }, []);

  // ---------------- Approver Email Change ----------------
  const handleApproverChange = (index, value) => {
    setCompliances((prev) => {
      const updated = [...prev];
      updated[index].approver_email = value;
      return updated;
    });
  };

  // ---------------- Send to Approver ----------------
  const handleSendToApprover = async (item, index) => {
    if (!item.approver_email) {
      alert("Enter approver email");
      return;
    }

    const formData = new FormData();
    formData.append("approver_email", item.approver_email);
    formData.append("compliance_instance_id", String(item.regcmp_id));

    if (files[index]) formData.append("attachment", files[index]);

    try {
      const res = await fetch(`${BASE_URL}/compliance/regulatory/send-to-approver`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      const data = await res.json();

      if (res.status !== 200) throw data;

      setSuccessMsg("✅ Sent to approver successfully");
      setErrorMsg("");

      // Clear uploaded file
      setFiles((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });

      fetchCompliances();
    } catch (err) {
      console.error(err);
      setErrorMsg(err.error || "❌ Failed to send to approver");
      setSuccessMsg("");
    }
  };

  // ---------------- Edit ----------------
  const handleEdit = (regcmpId) => {
    localStorage.setItem("regcmpId", regcmpId);
    navigate("/final-edit");
  };

  // ---------------- Delete All ----------------
const handleDeleteAll = async () => {
  const complianceId = localStorage.getItem("complianceId");

  if (!complianceId) {
    alert("Compliance ID not found");
    return;
  }

  const confirmDelete = window.confirm(
    "Are you sure you want to delete all regulatory compliance records?"
  );
  if (!confirmDelete) return;

  try {
    const res = await apiFetch(
      `/compliance/delete/regulatory/${complianceId}`,
      { method: "DELETE" }
    );

    setSuccessMsg(
      `✅ ${res?.instances_deleted || "All"} compliance records deleted successfully`
    );
    setErrorMsg("");

    // Clear table instantly
    setCompliances([]);

    // Optional refetch (safe)
    fetchCompliances();
  } catch (err) {
    console.error(err);
    setErrorMsg(err?.error || "❌ Failed to delete compliance records");
    setSuccessMsg("");
  }
};



  // ---------------- Auto-hide Success ----------------
  useEffect(() => {
    if (successMsg) {
      setSuccessVisible(true);
      const timer = setTimeout(() => setSuccessVisible(false), 4500);
      const removeTimer = setTimeout(() => setSuccessMsg(""), 5000);
      return () => {
        clearTimeout(timer);
        clearTimeout(removeTimer);
      };
    }
  }, [successMsg]);

  const statusCounts = useMemo(() => {
  const counts = {
    total: 0,
    pending: 0,
    requested: 0,
    approved: 0,
  };

  compliances.forEach((item) => {
    counts.total++;

    const status = item.status?.toLowerCase();
    if (status === "pending") counts.pending++;
    else if (status === "requested") counts.requested++;
    else if (status === "approved") counts.approved++;
  });

  return counts;
}, [compliances]);
  

  return (
    <div className="edit-compliance">
      <UserSidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <UserHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

        <main style={{ flex: 1, padding: "1rem 2rem", overflowY: "auto" }}>
          <header>
            <h2>Instances</h2>
          </header>

          {/* ---------------- Messages ---------------- */}
          <div className="messages-top">
            {errorMsg && <div className="login-error">{errorMsg}</div>}
            {successMsg && (
              <div className={`success-msg ${!successVisible ? "fade-out" : ""}`}>
                {successMsg}
              </div>
            )}
          </div>
          
    <div style={{
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: "10px"
}}>
  <div style={{ display: "flex", gap: "10px" }}>

    <div style={{
      background: "#e2e3e5",
      color: "#383d41",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Total: {statusCounts.total}
    </div>

    <div style={{
      background: "#f8d7da",
      color: "#721c24",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Pending: {statusCounts.pending}
    </div>

    <div style={{
      background: "#fff3cd",
      color: "#856404",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Requested: {statusCounts.requested}
    </div>

    <div style={{
      background: "#d4edda",
      color: "#155724",
      padding: "6px 12px",
      borderRadius: "20px",
      fontWeight: "600",
      fontSize: "13px"
    }}>
      Approved: {statusCounts.approved}
    </div>

  </div>
</div>
          <div className="table-wrapper">
            <table className="data-table" style={{ tableLayout: "fixed", width: "100%" }}>
  <thead>
    <tr>
      <th style={{ width: "180px" }}>INSTANCE</th>
      <th style={{ width: "400px" }}>DESCRIPTION</th> {/* increased width */}
      <th style={{ width: "100px" }}>START DATE</th>
      <th style={{ width: "100px" }}>ACTION DATE</th>
      <th style={{ width: "100px" }}>END DATE</th>
      <th style={{ width: "90px" }}>REM. BEFORE</th>
      <th style={{ width: "100px" }}>STATUS</th>
      <th style={{ width: "120px" }}>APPROVER’S EMAIL</th> {/* decreased */}
      <th style={{ width: "140px" }}>ATTACHMENT</th>       {/* decreased */}
      <th style={{ width: "120px" }}>ACTION</th>             {/* decreased */}
      <th style={{ width: "60px" }}>EDIT</th>
    </tr>
  </thead>
<tbody>
  {loading ? (
    <tr>
      <td colSpan="11" style={{ textAlign: "center" }}>
        Loading...
      </td>
    </tr>
  ) : compliances.length === 0 ? (
    <tr>
      <td colSpan="11" style={{ textAlign: "center" }}>
        No records found
      </td>
    </tr>
  ) : (
    compliances.map((item, index) => (
      <tr key={index}>
        {/* INSTANCE */}
       <td>
  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
    
    <span>
      {item.title && item.title.trim() !== ""
        ? item.title
        : item.particular}
    </span>

    {item.long_description && (
      <IoIosInformationCircle
        title={item.description}
        style={{
          cursor: "pointer",
          fontSize: "16px",
          color: "#1A1744"
        }}
      />
    )}

  </div>
</td>

        {/* DESCRIPTION */}
        <td style={{ maxWidth: "400px" }}>
          <div className="description-scroll">
            {item.long_description}
          </div>
        </td>

        <td>{item.start_date}</td>
        <td>{item.action_date}</td>
        <td>{item.end_date}</td>
        <td>{item.reminder}</td>

        {/* STATUS */}
        <td
          style={{
            color:
              item.status?.toLowerCase() === "approved"
                ? "green"
                : item.status?.toLowerCase() === "requested"
                ? "orange"
                : "red",
            fontWeight: 500,
          }}
        >
          {item.status}
        </td>

        {/* APPROVER EMAIL */}
        <td>
          <input
            type="email"
            className="approver-input"
            value={item.approver_email}
            onChange={(e) =>
              handleApproverChange(index, e.target.value)
            }
            placeholder="Enter email"
          />
        </td>

        {/* FILE UPLOAD */}
        <td>
          <label className="file-upload-label">
            <input
              type="file"
              onChange={(e) =>
                setFiles((prev) => ({
                  ...prev,
                  [index]: e.target.files[0],
                }))
              }
              hidden
            />
            <span className="custom-file-btn">
              {files[index]
                ? files[index].name
                : "Attach evidence"}
            </span>
          </label>
        </td>

        {/* SEND BUTTON */}
        <td>
          <button
            className="action-btn primary"
            onClick={() =>
              handleSendToApprover(item, index)
            }
            title="Send to approver"
          >
            ✔
          </button>
        </td>

        {/* EDIT BUTTON */}
        <td>
          <button
            className="action-btn edit"
            onClick={() => handleEdit(item.regcmp_id)}
            disabled={
              item.status?.toLowerCase() === "approved" ||
              item.status?.toLowerCase() === "requested"
            }
            style={{
              cursor:
                item.status?.toLowerCase() === "approved" ||
                item.status?.toLowerCase() === "requested"
                  ? "not-allowed"
                  : "pointer",
              opacity:
                item.status?.toLowerCase() === "approved" ||
                item.status?.toLowerCase() === "requested"
                  ? 0.5
                  : 1,
            }}
          >
            ✎
          </button>
        </td>
      </tr>
    ))
  )}
</tbody>
</table>

          </div>

          <div style={{ textAlign: "center", marginTop: "25px" }}>
            <button className="submit-btn" style={{ width: "95%" }} onClick={handleDeleteAll}>
              Delete All
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
